import type { ImagePrice, ImagePricing } from "@nlc/shared-types";

import { useState } from "react";

import { Button } from "@/components/button";
import { Dialog, DialogTrigger } from "@/components/dialog";
import { NumberField } from "@/components/number-field";
import { Popover } from "@/components/popover";
import { Switch } from "@/components/switch";

const USD_FORMAT = { maximumFractionDigits: 6, minimumFractionDigits: 2 };

interface PriceEditorProps {
  label: string;
  onSave: (price: ImagePrice) => void;
  price: ImagePrice;
}

export function PriceEditor({ label, onSave, price }: PriceEditorProps) {
  return (
    <DialogTrigger>
      <Button
        aria-label={`Edit ${label} price`}
        color="gray"
        size="sm"
        variant="tertiary"
      >
        Edit
      </Button>
      <Popover placement="bottom start">
        <Dialog aria-label={`${label} price`} className="w-72 p-lg">
          {({ close }) => (
            <PriceForm
              onCancel={close}
              onSave={(next) => {
                onSave(next);
                close();
              }}
              price={price}
            />
          )}
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}

function PriceForm({
  onCancel,
  onSave,
  price,
}: {
  onCancel: () => void;
  onSave: (price: ImagePrice) => void;
  price: ImagePrice;
}) {
  const [pricing, setPricing] = useState<ImagePricing>(price.pricing);
  const [inputImageUsd, setInputImageUsd] = useState(price.inputImages?.usd);
  const [verified, setVerified] = useState(price.verified);

  const save = () =>
    onSave({
      ...price,
      inputImages:
        price.inputImages && inputImageUsd !== undefined
          ? { ...price.inputImages, usd: inputImageUsd }
          : price.inputImages,
      pricing,
      updatedAt: new Date(),
      verified,
    });

  return (
    <form
      className="flex flex-col gap-md"
      onSubmit={(event) => {
        event.preventDefault();
        save();
      }}
    >
      <PricingFields onChange={setPricing} pricing={pricing} />
      {price.inputImages && (
        <UsdField
          label="Per input image (USD)"
          onChange={setInputImageUsd}
          value={inputImageUsd ?? 0}
        />
      )}
      <Switch isSelected={verified} onChange={setVerified}>
        Verified
      </Switch>
      <div className="flex justify-end gap-md">
        <Button color="gray" onPress={onCancel} size="sm" variant="secondary">
          Cancel
        </Button>
        <Button size="sm" type="submit">
          Apply
        </Button>
      </div>
    </form>
  );
}

function UsdField({
  label,
  onChange,
  value,
}: {
  label: string;
  onChange: (value: number) => void;
  value: number;
}) {
  return (
    <NumberField
      formatOptions={USD_FORMAT}
      label={label}
      minValue={0}
      onChange={(next) => !Number.isNaN(next) && onChange(next)}
      value={value}
    />
  );
}

function PricingFields({
  onChange,
  pricing,
}: {
  onChange: (pricing: ImagePricing) => void;
  pricing: ImagePricing;
}) {
  switch (pricing.kind) {
    case "perImage": {
      return (
        <UsdField
          label="Per image (USD)"
          onChange={(usd) => onChange({ ...pricing, usd })}
          value={pricing.usd}
        />
      );
    }
    case "perImageByArea": {
      return (
        <>
          {pricing.tiers.map((tier, index) => (
            <UsdField
              key={tier.maxPixels}
              label={`Up to ${tier.maxPixels.toLocaleString("en-US")} px (USD)`}
              onChange={(usd) =>
                onChange({
                  ...pricing,
                  tiers: pricing.tiers.map((current, position) =>
                    position === index ? { ...current, usd } : current,
                  ),
                })
              }
              value={tier.usd}
            />
          ))}
        </>
      );
    }
    case "perMegapixel": {
      return (
        <>
          <UsdField
            label="First megapixel (USD)"
            onChange={(firstUsd) => onChange({ ...pricing, firstUsd })}
            value={pricing.firstUsd}
          />
          <UsdField
            label="Each additional megapixel (USD)"
            onChange={(additionalUsd) =>
              onChange({ ...pricing, additionalUsd })
            }
            value={pricing.additionalUsd}
          />
        </>
      );
    }
  }
}
